/** Choosing who is in a circle: the people row of the sheet, and the list inside it with a tick for each person. */
import { type ReactNode, useState } from "react";
import { Button } from "../components/ui/Button";
import { Face, FaceStack } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import type { TalkFace } from "../../shared/talk";
import { facesOf } from "./parts";

/** A person in the list, with the one short fact that helps the host choose. */
export interface Pickable {
  registrantId: string;
  name: string;
  photoUrl: string | null;
  fact: string;
}

/** The people row: who is in as faces and a count, with Choose when the people can be listed. */
export function PeopleRow({ faces, count, total, note, onChoose }: { faces: TalkFace[]; count: number; total: number; note: ReactNode; onChoose?: () => void }) {
  return (
    <div className="pk-row">
      <FaceStack people={facesOf(faces.slice(0, 4))} size={34} more={Math.max(0, count - 4)} />
      <span className="q-rc-t">
        <b>{count === total ? `${total.toLocaleString("en-US")} ${total === 1 ? "person" : "people"}` : `${count.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} people`}</b>
        <small>{note}</small>
      </span>
      {onChoose ? (
        <Button size="s" aria-label="Choose who is in the circle" onClick={onChoose}>
          Choose
        </Button>
      ) : null}
    </div>
  );
}

/** The list: a name box, All N, and each person with a tick and a fact. */
export function PickList({ people, chosen, onToggle, onAll }: { people: Pickable[]; chosen: ReadonlySet<string>; onToggle: (id: string) => void; onAll: (on: boolean) => void }) {
  const [q, setQ] = useState("");
  const word = q.trim().toLowerCase();
  const shown = word ? people.filter((x) => x.name.toLowerCase().includes(word)) : people;
  const all = chosen.size === people.length;
  return (
    <div className="pk">
      <label className="fsearch pk-q">
        <Icon name="search" />
        <input aria-label="Find a name" placeholder="Find a name" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <label className="pk-all">
        <input
          type="checkbox"
          checked={all}
          ref={(el) => {
            if (el) el.indeterminate = chosen.size > 0 && !all;
          }}
          onChange={() => onAll(!all)}
        />
        <b>All {people.length.toLocaleString("en-US")}</b>
      </label>
      <div className="pk-list">
        {shown.map((x) => (
          <label key={x.registrantId} className="pk-p">
            <input type="checkbox" checked={chosen.has(x.registrantId)} onChange={() => onToggle(x.registrantId)} />
            <Face name={x.name} photo={x.photoUrl} size={36} />
            <span className="pk-t">
              <b>{x.name}</b>
              <small>{x.fact}</small>
            </span>
          </label>
        ))}
        {shown.length ? null : <p className="pk-none">No one is called "{q.trim()}"</p>}
      </div>
    </div>
  );
}
